'use client';

import { StepsFlowEnum } from '@/config/steps.config';
import useConfig from '@/hooks/useConfig';
import { BookingCountryService, BookingShop } from '@repo/common/interfaces';
import { useWidgetStore } from '@/providers/widget.provider';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { VpLoader } from '@vtmn-play/react';
import { cleanObject } from '@repo/common/helpers';
import { useTrackingAnalytics } from '@/providers/tracking.provider';
import { TrackingEvent } from '@/interfaces/tracking.interface';

interface WidgetStepProps {
  countryCode: string;
  mode?: StepsFlowEnum;
  countryServices?: BookingCountryService[];
  shop?: BookingShop;
  storeId?: string;
  modelId?: string;
  superModelId?: string;
}

const WidgetStep = ({
  shop,
  countryServices,
  mode,
  countryCode,
  storeId,
  modelId,
  superModelId,
}: WidgetStepProps) => {
  const { nextUrl, goToNextStep } = useConfig();
  const {
    setShop,
    setCountryService,
    setStoreId,
    setModelId,
    setSuperModelId,
    reset,
  } = useWidgetStore((state) => state);
  const router = useRouter();
  const { trackEvent } = useTrackingAnalytics();

  useEffect(() => {
    if (!nextUrl) {
      return;
    }

    // Reset store on new reservation
    reset();

    const selectShop = () => {
      setStoreId(storeId);
      setShop(shop);
      trackEvent(TrackingEvent.STORE_SELECTED, {
        store_id: shop?.id,
        store_name: shop?.attributes?.name ?? shop?.storeId,
      });
      goToNextStep(
        new URLSearchParams(
          cleanObject({
            shop: shop?.id,
          }),
        ),
      );
    };

    switch (mode) {
      case StepsFlowEnum.STORE_LOCATOR:
        if (shop) {
          selectShop();
          return;
        }
        router.replace(`/country/${countryCode}`);
        return;
      case StepsFlowEnum.SERVICE:
        if (countryServices?.length === 1) {
          const countryService = countryServices?.[0];
          setModelId(modelId);
          setCountryService(countryService);
          goToNextStep(
            new URLSearchParams(
              cleanObject({
                sku: countryService?.sku,
                modelId,
              }),
            ),
          );
          return;
        }
        router.replace(`/country/${countryCode}`);
        return;
      case StepsFlowEnum.SUPER_MODEL:
        setSuperModelId(superModelId);

        if (countryServices?.length === 1) {
          const countryService = countryServices?.[0];
          setCountryService(countryService);
          const searchParams = new URLSearchParams(
            cleanObject({
              mode,
              superModelId,
              sku: countryService?.sku,
            }),
          );
          router.replace(
            `/country/${countryCode}/steps/informations?${searchParams.toString()}`,
          );
          return;
        }

        if (countryServices && countryServices?.length > 1) {
          goToNextStep(
            new URLSearchParams(
              cleanObject({
                superModelId,
              }),
            ),
          );
          return;
        }

        router.replace(`/country/${countryCode}`);
        return;
      case StepsFlowEnum.BOOK_FOR_OTHER:
        goToNextStep();
        return;
      case StepsFlowEnum.BOOK_FOR_OTHER_WITH_STORE:
        if (shop) {
          selectShop();
          return;
        }
        break;
      default:
        break;
    }

    router.replace(nextUrl);
  }, [
    shop,
    countryServices,
    mode,
    nextUrl,
    modelId,
    storeId,
    countryCode,
    setModelId,
    setSuperModelId,
    setStoreId,
    setCountryService,
    setShop,
    trackEvent,
  ]);

  return (
    <div className='flex justify-center items-center mt-4'>
      <VpLoader role='status' />
    </div>
  );
};

export default WidgetStep;
